import { useWindowEvent } from "@mantine/hooks";
import { useState } from "react";
import * as Analytics from "../../../Utils/Analytics";
import { useIsMobile } from "./useIsMobile";

enum EOutcomeInstallPrompt {
	dismissed = "dismissed",
	accepted = "accepted"
}

interface IBeforeInstallPrompt extends CustomEvent<unknown> {
	prompt: () => void;
	userChoice: Promise<{ outcome: EOutcomeInstallPrompt }>;
}

/**
 * Хук для отображения кастомного окна установки приложения
 */
export const useInstallPrompt = (game: string) => {
	const [deferredPrompt, setDeferredPrompt] = useState<IBeforeInstallPrompt | null>(null);
	const [canInstall, setCanInstall] = useState(false);

	const isMobile = useIsMobile();

	// Функция нажатия на установку (игрок решил установить приложение на нашей кастомной форме)
	const showPrompt = async () => {
		// Скрываем кастомную форму
		setCanInstall(false);
		if (deferredPrompt === null) return;
		// Показываем снова но уже стандартное окно установки
		deferredPrompt.prompt();
		// Ждем ответа от игрока
		const { outcome } = await deferredPrompt.userChoice;
		log.info(`User response to the install prompt: ${outcome}`);
		if (outcome === EOutcomeInstallPrompt.accepted) {
			void Analytics.site.install({ game });
		}
		// Скидываем отложенный диалог (т.к. дважды вызвать у него prompt нельзя)
		setDeferredPrompt(null);

		return outcome;
	};

	useWindowEvent("appinstalled", () => {
		setCanInstall(false);
		setDeferredPrompt(null);
		log.info("PWA was installed");
	});

	useWindowEvent("beforeinstallprompt", (event) => {
		// Блокируем стандартное отображение окна установки
		event.preventDefault();
		if (isMobile) {
			// Установливаем отложенный диалог установки
			setDeferredPrompt(event as IBeforeInstallPrompt);
			// Говорим приложению, что можно отобразить кастомную установку
			setCanInstall(true);
			log.info("Scheduling install prompt");
		} else {
			setCanInstall(false);
			setDeferredPrompt(null);
			log.info("Not showing install prompt not on mobile");
		}
		log.info(`'beforeinstallprompt' event was fired.`);
	});

	return {
		canInstall,
		showPrompt
	};
};
